import { markdownFileName } from "client-server-shared/constants";
import { blobToBase64 } from "utils/image-utils";
import { htmlToMarkdown } from "client-server-shared/utils/parser";

let jsZipPromise: any;
let saveAsPromise: any;
let pdfExporterPromise: any;

let quillToWordPromise: any;

export const loadJsZip = () => {
  if (!jsZipPromise) {
    jsZipPromise = import("jszip").then((m) => m.default);
  }

  return jsZipPromise;
};

const loadSaveAs = () => {
  if (!saveAsPromise) {
    saveAsPromise = import("utils/save-as").then((m) => m.default);
  }

  return saveAsPromise;
};

const loadPdfDependencies = async () => {
  const [saveAs] = await Promise.all([loadSaveAs()]);

  return { saveAs };
};

const loadWordDependencies = async () => {
  const [saveAs] = await Promise.all([loadSaveAs()]);
  return {
    saveAs,
  };
};

const loadJsZipDependencies = async () => {
  const [JSZip, saveAs] = await Promise.all([loadJsZip(), loadSaveAs()]);

  return { JSZip, saveAs };
};

const downloadImage = async (url: string) => {
  const response = await fetch(url);
  const blob = await response.blob();
  return blob;
};

export async function processHTMLStrToBase64(
  htmlStr: string,
  innerContentOnly = false
) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlStr, "text/html");
  const images = doc.getElementsByTagName("img");

  if (!innerContentOnly) {
    await Promise.all(
      Object.entries(images).map(async (image) => {
        const [index, img] = image;
        const imageBlob = await downloadImage(img.src);
        const base64 = await blobToBase64(imageBlob);

        // Update the image src to use the relative path
        img.src = base64;
      })
    ).catch((e) => {});
  }

  const serializer = new XMLSerializer();

  if (innerContentOnly) {
    return doc.body.innerHTML;
  }

  const content = serializer.serializeToString(doc);
  return content;
}

async function processHtmlStr(htmlStr: string) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlStr, "text/html");
  const images = doc.getElementsByTagName("img");

  const imagesByFileName: Record<
    string,
    { blob: Blob; img: HTMLImageElement }
  > = {};

  await Promise.all(
    Object.entries(images).map(async (image) => {
      const [index, img] = image;
      const imageBlob = await downloadImage(img.src);
      const imageName = `image${index}.png`;

      // Update the image src to use the relative path
      img.src = `./images/${imageName}`;

      // Download the image and save it in the images folder
      if (!imagesByFileName[imageName]) {
        imagesByFileName[imageName] = { blob: imageBlob, img };
      }
    })
  ).catch((e) => {});

  const serializer = new XMLSerializer();
  const content = serializer.serializeToString(doc);

  return {
    content,
    imagesByFileName,
  };
}

export const toHtml = async (htmlStr: string, fileName: string) => {
  const { JSZip, saveAs } = await loadJsZipDependencies();
  const zip = new JSZip();

  const { content, imagesByFileName } = await processHtmlStr(htmlStr);
  const imageFolder = zip.folder("images");

  for (const key in imagesByFileName) {
    const imageBlob = imagesByFileName[key].blob;
    imageFolder?.file(key, imageBlob);
  }
  zip.file("index.html", content);
  const zipBlob = await zip.generateAsync({ type: "blob" });
  saveAs(zipBlob, `${fileName}.zip`);
};

export const toMarkdown = async (htmlStr: string, fileName: string) => {
  const { JSZip, saveAs } = await loadJsZipDependencies();

  const zip = new JSZip();

  const { content, imagesByFileName } = await processHtmlStr(htmlStr);
  const imageFolder = zip.folder("images");

  for (const key in imagesByFileName) {
    const imageBlob = imagesByFileName[key].blob;
    imageFolder?.file(key, imageBlob);
  }
  const markdownContent = await htmlToMarkdown(content);
  zip.file(markdownFileName, markdownContent);
  const zipBlob = await zip.generateAsync({ type: "blob" });
  saveAs(zipBlob, `${fileName}.zip`);
};

const processQuillDelta = async (delta: any) => {
  const optsWithImages = await Promise.all(
    delta.ops.map(async (op) => {
      if (op.insert.image) {
        if (op.insert.image.startsWith("data:image")) {
          return op;
        }

        try {
          const blob = await downloadImage(op.insert.image);

          const base64 = await blobToBase64(blob);
          return {
            ...op,
            insert: {
              ...op.insert,
              image: base64,
            },
          };
        } catch (e) {
          return null;
        }
      }
      return op;
    })
  );

  return {
    ...delta,
    ops: optsWithImages.filter((op) => op !== null),
  };
};

export const toWord = async (
  fileName: string,
  getQuillInstance: () => any | undefined
) => {
  return;
};

export const toPDF = async (
  fileName: string,
  getQuillInstance: () => any | undefined
) => {
  if (!getQuillInstance) {
    return;
  }

  const quill = getQuillInstance();

  if (!quill) {
    return;
  }

  const delta = quill.getContents(); // gets the Quill delta

  if (!delta || !delta.ops) {
    return;
  }

  let optsWithImages = await Promise.all(
    delta.ops.map(async (op) => {
      if (!op.attributes) {
        op.attributes = {};
      }
      if (op.insert.image) {
        if (op.insert.image.startsWith("data:image")) {
          return op;
        }

        try {
          const blob = await downloadImage(op.insert.image);

          const jpegBase64 = await blobToJpegBase64(blob);

          return {
            ...op,
            insert: {
              ...op.insert,
              image: jpegBase64,
            },
          };
        } catch (e) {
          return null;
        }
      }
      return op;
    })
  );
  optsWithImages = optsWithImages.filter((op) => op !== null);
  if (optsWithImages.length > 0) {
    if (optsWithImages[0].insert === "\n") {
      // optsWithImages = optsWithImages.slice(1);
    }
  }
  const { saveAs } = await loadPdfDependencies();

  const pdfAsBlob = await pdfExporter.generatePdf({
    ...delta,
    ops: optsWithImages,
  });
  saveAs(pdfAsBlob, fileName);
};

async function blobToJpegBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = function () {
      const img = new Image();

      img.onload = function () {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;

        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);

        // Convert the canvas content to JPEG format
        const jpegBase64 = canvas.toDataURL("image/jpeg");
        resolve(jpegBase64);
      };

      img.src = reader.result;
    };

    reader.readAsDataURL(blob);
  });
}
